import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  AppraisalTemplateAssignmentDto,
  AppraisalTemplateAssignmentCreateDto,
  AppraisalTemplateAssignmentDepartmentDto
} from '../models';

@Injectable()
export class AppraisalTemplateAssignmentService {
  private readonly API_BASE = `${environment.BASE_URL}/appraisal-template-assignments`;

  constructor(private http: HttpClient) {}

  /**
   * Get all assignments for a template
   * @param templateId Template ID
   * @returns Observable of assignment list
   */
  getByTemplateId(templateId: number): Observable<AppraisalTemplateAssignmentDto[]> {
    if (!templateId || templateId <= 0) {
      return throwError(() => new Error('Invalid form ID'));
    }

    return this.http
      .get<AppraisalTemplateAssignmentDto[]>(`${this.API_BASE}/template/${templateId}`)
      .pipe(
        map((result: any) => result?.data || result || []),
        catchError(this.handleError)
      );
  }

  /**
   * Get all assignments for a department
   * @param departmentId Department ID
   * @returns Observable of assignment list
   */
  getByDepartmentId(departmentId: number): Observable<AppraisalTemplateAssignmentDto[]> {
    if (!departmentId || departmentId <= 0) {
      return throwError(() => new Error('Invalid department ID'));
    }

    return this.http
      .get<AppraisalTemplateAssignmentDto[]>(`${this.API_BASE}/department/${departmentId}`)
      .pipe(
        map((result: any) => result?.data || result || []),
        catchError(this.handleError)
      );
  }

  /**
   * Get a specific assignment by ID
   * @param id Assignment ID
   * @returns Observable of assignment details
   */
  getById(id: number): Observable<AppraisalTemplateAssignmentDto> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid assignment ID'));
    }

    return this.http
      .get<AppraisalTemplateAssignmentDto>(`${this.API_BASE}/${id}`)
      .pipe(
        map((result: any) => result?.data || result),
        catchError(this.handleError)
      );
  }

  /**
   * Get available departments for assignment
   * @param businessId Business ID
   * @param templateId Optional template ID to exclude already assigned departments
   * @returns Observable of available departments
   */
  getAvailableDepartments(
    businessId: number,
    templateId?: number
  ): Observable<AppraisalTemplateAssignmentDepartmentDto[]> {
    if (!businessId || businessId <= 0) {
      return throwError(() => new Error('Invalid business ID'));
    }

    let url = `${this.API_BASE}/available-departments/${businessId}`;
    if (templateId && templateId > 0) {
      url += `?templateId=${templateId}`;
    }

    return this.http
      .get<AppraisalTemplateAssignmentDepartmentDto[]>(url)
      .pipe(
        map((result: any) => result?.data || result || []),
        catchError(this.handleError)
      );
  }

  /**
   * Create a new assignment
   * @param templateId Template ID
   * @param assignment Assignment data
   * @returns Observable of created assignment
   */
  create(templateId: number, assignment: AppraisalTemplateAssignmentCreateDto): Observable<AppraisalTemplateAssignmentDto> {
    if (!templateId || templateId <= 0) {
      return throwError(() => new Error('Invalid form ID'));
    }

    if (!assignment) {
      return throwError(() => new Error('Assignment data is required'));
    }

    if (assignment.priority < 0) {
      return throwError(() => new Error('Priority must be a non-negative number'));
    }

    return this.http
      .post<AppraisalTemplateAssignmentDto>(`${this.API_BASE}/template/${templateId}`, assignment)
      .pipe(
        map((result: any) => result?.data || result),
        catchError(this.handleError)
      );
  }

  /**
   * Create multiple assignments at once
   * @param templateId Template ID
   * @param assignments Array of assignment data
   * @returns Observable of created assignments
   */
  createBatch(
    templateId: number,
    assignments: AppraisalTemplateAssignmentCreateDto[]
  ): Observable<AppraisalTemplateAssignmentDto[]> {
    if (!templateId || templateId <= 0) {
      return throwError(() => new Error('Invalid form ID'));
    }

    if (!assignments || assignments.length === 0) {
      return throwError(() => new Error('At least one assignment is required'));
    }

    return this.http
      .post<AppraisalTemplateAssignmentDto[]>(`${this.API_BASE}/template/${templateId}/batch`, assignments)
      .pipe(
        map((result: any) => result?.data || result || []),
        catchError(this.handleError)
      );
  }

  /**
   * Update an existing assignment
   * @param id Assignment ID
   * @param assignment Updated assignment data
   * @returns Observable of updated assignment
   */
  update(id: number, assignment: AppraisalTemplateAssignmentCreateDto): Observable<AppraisalTemplateAssignmentDto> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid assignment ID'));
    }

    if (!assignment) {
      return throwError(() => new Error('Assignment data is required'));
    }

    if (assignment.priority < 0) {
      return throwError(() => new Error('Priority must be a non-negative number'));
    }

    return this.http
      .put<AppraisalTemplateAssignmentDto>(`${this.API_BASE}/${id}`, assignment)
      .pipe(
        map((result: any) => result?.data || result),
        catchError(this.handleError)
      );
  }

  /**
   * Update assignment priority
   * @param id Assignment ID
   * @param priority New priority value
   * @returns Observable of updated assignment
   */
  updatePriority(id: number, priority: number): Observable<AppraisalTemplateAssignmentDto> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid assignment ID'));
    }

    if (priority < 0) {
      return throwError(() => new Error('Priority must be a non-negative number'));
    }

    return this.http
      .patch<AppraisalTemplateAssignmentDto>(`${this.API_BASE}/${id}/priority`, { priority })
      .pipe(
        map((result: any) => result?.data || result),
        catchError(this.handleError)
      );
  }

  /**
   * Delete an assignment
   * @param id Assignment ID
   * @returns Observable of deletion result
   */
  delete(id: number): Observable<any> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid assignment ID'));
    }

    return this.http
      .delete(`${this.API_BASE}/${id}`)
      .pipe(
        map((result: any) => result),
        catchError(this.handleError)
      );
  }

  /**
   * Delete multiple assignments
   * @param ids Array of assignment IDs
   * @returns Observable of deletion result
   */
  deleteBatch(ids: number[]): Observable<any> {
    if (!ids || ids.length === 0) {
      return throwError(() => new Error('At least one assignment ID is required'));
    }

    return this.http
      .request('delete', this.API_BASE, { body: { ids } })
      .pipe(
        map((result: any) => result),
        catchError(this.handleError)
      );
  }

  /**
   * Remove all assignments for a template
   * @param templateId Template ID
   * @returns Observable of deletion result
   */
  deleteByTemplateId(templateId: number): Observable<any> {
    if (!templateId || templateId <= 0) {
      return throwError(() => new Error('Invalid form ID'));
    }

    return this.http
      .delete(`${this.API_BASE}/template/${templateId}`)
      .pipe(
        map((result: any) => result),
        catchError(this.handleError)
      );
  }

  /**
   * Get the highest priority template assigned to a department
   * @param departmentId Department ID
   * @returns Observable of template assignment
   */
  getHighestPriorityForDepartment(departmentId: number): Observable<AppraisalTemplateAssignmentDto | null> {
    if (!departmentId || departmentId <= 0) {
      return throwError(() => new Error('Invalid department ID'));
    }

    return this.http
      .get<AppraisalTemplateAssignmentDto>(`${this.API_BASE}/department/${departmentId}/highest-priority`)
      .pipe(
        map((result: any) => result?.data || result || null),
        catchError(this.handleError)
      );
  }

  /**
   * Check if a department is already assigned to a template
   * @param templateId Template ID
   * @param departmentId Department ID
   * @returns Observable of boolean indicating if assignment exists
   */
  checkAssignmentExists(templateId: number, departmentId: number): Observable<boolean> {
    if (!templateId || templateId <= 0) {
      return throwError(() => new Error('Invalid form ID'));
    }

    if (!departmentId || departmentId <= 0) {
      return throwError(() => new Error('Invalid department ID'));
    }

    return this.http
      .get<boolean>(`${this.API_BASE}/check-exists`, {
        params: {
          templateId: templateId.toString(),
          departmentId: departmentId.toString()
        }
      })
      .pipe(
        map((result: any) => result?.exists ?? result),
        catchError(this.handleError)
      );
  }

  /**
   * Handle HTTP errors
   * @param error HTTP error response
   * @returns Observable error
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Client Error: ${error.error.message}`;
    } else {
      if (error.status === 0) {
        errorMessage = 'Unable to connect to the server. Please check your connection.';
      } else if (error.status === 400) {
        errorMessage = error.error?.message || 'Invalid request data';
      } else if (error.status === 404) {
        errorMessage = 'Assignment not found.';
      } else if (error.status === 409) {
        errorMessage = error.error?.message || 'Department is already assigned to this form.';
      } else if (error.status === 422) {
        errorMessage = error.error?.message || 'Validation failed. Please check your input.';
      } else if (error.status >= 500) {
        errorMessage = 'A server error occurred. Please try again later.';
      } else {
        errorMessage = error.error?.message || `Server Error: ${error.status}`;
      }
    }

    console.error('AppraisalTemplateAssignmentService Error:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }
}
